import { z } from "zod";

export const PASSWORD_MIN_LENGTH = 10;

export const TRADES = ["Elektro", "SHK", "Bau", "Dach", "Maler", "Tischler", "Anderes"] as const;
export const EMPLOYEE_RANGES = ["1", "2 bis 5", "6 bis 15", "16 bis 30", "mehr als 30"] as const;

const text = (min: number, max: number, msg: string) => z.string().trim().min(min, msg).max(max, msg);

const password = z
  .string()
  .min(PASSWORD_MIN_LENGTH, `Das Passwort braucht mindestens ${PASSWORD_MIN_LENGTH} Zeichen.`)
  .max(200, "Das Passwort ist zu lang.");

export const registrationSchema = z.object({
  companyName: text(2, 120, "Bitte geben Sie den Firmennamen an."),
  trade: z.enum(TRADES).optional(),
  contactName: text(2, 120, "Bitte geben Sie Vor- und Nachnamen an."),
  email: z.string().max(254),
  phone: text(5, 40, "Bitte geben Sie eine Telefonnummer an."),
  password,
  employeeRange: z.enum(EMPLOYEE_RANGES).optional(),
  acceptTerms: z.literal(true, { error: "Bitte bestätigen Sie, dass Sie als Unternehmer handeln, und stimmen Sie den Nutzungsbedingungen und der Auftragsverarbeitung zu." }),
  /** Unsichtbares Feld: Menschen lassen es leer, Bots füllen es. */
  website: z.string().max(200).optional(),
  /** Millisekunden zwischen Öffnen und Absenden des Formulars. */
  formMs: z.number().int().nonnegative().optional(),
});
export type RegistrationInput = z.infer<typeof registrationSchema>;

/** Anmeldung als Privatperson („HandVero Zuhause“). Kein Firmenname, keine Telefonnummer, keine Unternehmer-Erklärung. */
export const privateRegistrationSchema = z.object({
  name: text(2, 120, "Bitte geben Sie Ihren Vor- und Nachnamen an."),
  email: z.string().max(254),
  password,
  acceptTerms: z.literal(true, { error: "Bitte stimmen Sie den Nutzungsbedingungen zu und bestätigen Sie, dass Sie die Widerrufsbelehrung und die Datenschutzhinweise gelesen haben." }),
  acceptAds: z.literal(true, { error: "Bitte bestätigen Sie den Hinweis zu Werbung in der kostenlosen Version." }),
  /** Freiwillig: Hinweise und Angebote per E-Mail. */
  marketingMail: z.boolean().optional(),
  website: z.string().max(200).optional(),
  formMs: z.number().int().nonnegative().optional(),
});
export type PrivateRegistrationInput = z.infer<typeof privateRegistrationSchema>;

export const loginSchema = z.object({
  email: z.string().max(254),
  password: z.string().min(1, "Bitte geben Sie Ihr Passwort ein.").max(200),
});

export const emailOnlySchema = z.object({ email: z.string().max(254) });

export const tokenSchema = z.object({ token: z.string().min(20).max(200) });

export const resetPasswordSchema = z.object({ token: z.string().min(20).max(200), password });

export const inviteSchema = z.object({ email: z.string().max(254) });

export const acceptInviteSchema = z.object({
  token: z.string().min(20).max(200),
  name: text(2, 120, "Bitte geben Sie Vor- und Nachnamen an."),
  password,
});

/** Erste verständliche Fehlermeldung aus einer Zod-Prüfung. */
export function firstIssue(error: z.ZodError): string {
  return error.issues[0]?.message ?? "Bitte prüfen Sie Ihre Eingaben.";
}

/** Mein Konto */
export const profileSchema = z.object({ name: text(2, 120, "Bitte geben Sie Vor- und Nachnamen an.") });
export const changePasswordSchema = z.object({
  current: z.string().min(1, "Bitte geben Sie Ihr bisheriges Passwort ein.").max(200),
  next: password,
});
export const confirmPasswordSchema = z.object({ password: z.string().min(1, "Bitte bestätigen Sie mit Ihrem Passwort.").max(200) });
export const deleteCompanySchema = z.object({
  password: z.string().min(1, "Bitte bestätigen Sie mit Ihrem Passwort.").max(200),
  companyName: z.string().trim().max(200),
});
export const memberRoleSchema = z.object({ role: z.enum(["admin", "employee"]) });
